import { createTheme, ThemeProvider, Theme, useTheme } from '@mui/material/styles';
import { ReactNode, useMemo } from 'react';

/** Density presets controlling spacing throughout forms */
export const DENSITY_PRESETS = {
  compact: {
    fieldMarginBottom: 2,
    sectionPadding: 1.5,
    sectionMarginBottom: 1.5,
    gridGap: 1,
    sectionHeaderMarginBottom: 0.5,
    inputFontSize: '0.8125rem',
    labelFontSize: '0.75rem',
  },
  standard: {
    fieldMarginBottom: 4,
    sectionPadding: 2,
    sectionMarginBottom: 2,
    gridGap: 2,
    sectionHeaderMarginBottom: 1,
    inputFontSize: '0.875rem',
    labelFontSize: '0.8125rem',
  },
  comfortable: {
    fieldMarginBottom: 8,
    sectionPadding: 3,
    sectionMarginBottom: 3,
    gridGap: 2,
    sectionHeaderMarginBottom: 1.5,
    inputFontSize: '0.875rem',
    labelFontSize: '0.875rem',
  },
} as const;

export type Density = keyof typeof DENSITY_PRESETS;
export type Variant = 'standard' | 'outlined' | 'filled';

/**
 * Responsive form layout.
 *
 * A form's width has little to do with the viewport. The detail view's form column shrinks when a
 * related-view drawer opens beside it, the reference peek panel is a slide-in the user drags, a
 * child-grid detail panel sits under a grid. Viewport breakpoints cannot see any of that — the
 * window stays wide while the form itself gets 400px — so the grid renderers size their columns
 * with `@container` rules, and every host whose form area can be squeezed marks it with
 * {@link FORM_CONTAINER_SX}. Without such an ancestor the rules are inert and the viewport
 * fallback in the renderers applies, so full-page forms on plain pages are unaffected.
 *
 * The ladder is derived rather than hand-picked: a control stays readable down to
 * {@link FORM_FIELD_MIN_WIDTH} and a section is worth placing beside a sibling down to
 * {@link FORM_SECTION_MIN_WIDTH}, so a row may hold N of them only while the container is at
 * least N * min wide. Crossing each threshold widens the spans by one step — those steps are the
 * intermediate states between "everything side by side" and "everything stacked".
 */

/** Below this a control's label and value start truncating (measured on the widest lookups). */
export const FORM_FIELD_MIN_WIDTH = 220;
/** Padding and gap a section spends before any of its width reaches a control. */
const FORM_SECTION_CHROME = 56;
/**
 * A section is only worth placing beside a sibling while it can still hold two controls — put the
 * threshold any lower and widening the form makes it *less* dense, because the sections pair up
 * before either can keep its controls side by side.
 */
export const FORM_SECTION_MIN_WIDTH = 2 * FORM_FIELD_MIN_WIDTH + FORM_SECTION_CHROME;

/**
 * Marks a form area as the box the `@container` rules above measure. Put it on the element whose
 * width actually tracks the available form width — the scrolling content region of a detail view,
 * a panel body, a dialog's content — not on an inner wrapper.
 */
export const FORM_CONTAINER_SX = { containerType: 'inline-size' } as const;

interface SpanStep {
  /** While fewer than this many min-width items fit across the container… */
  perRow: number;
  /** …no item may be narrower than this many of the 12 columns. */
  span: number;
}

/** Controls: 6 per row → 4 → 3 → 2 → 1. */
const FIELD_STEPS: SpanStep[] = [
  { perRow: 6, span: 3 },
  { perRow: 4, span: 4 },
  { perRow: 3, span: 6 },
  { perRow: 2, span: 12 },
];

/** Sections: 3 per row → 2 → 1. */
const SECTION_STEPS: SpanStep[] = [
  { perRow: 3, span: 6 },
  { perRow: 2, span: 12 },
];

function ladderSx(steps: SpanStep[], minWidth: number, configured: number) {
  const sx: Record<string, { gridColumn: string }> = {};
  for (const { perRow, span } of steps) {
    // Widest threshold first: every query here is a max-width, so several match at once and the
    // last one declared wins the cascade — which must be the narrowest.
    sx[`@container (max-width: ${perRow * minWidth}px)`] = {
      gridColumn: `span ${Math.min(12, Math.max(configured, span))}`,
    };
  }
  return sx;
}

/** Responsive `gridColumn` for one control inside a section's 12-column grid. */
export const fieldSpanSx = (configured: number) => ladderSx(FIELD_STEPS, FORM_FIELD_MIN_WIDTH, configured);

/** Responsive `gridColumn` for one section inside a panel's 12-column grid. */
export const sectionSpanSx = (configured: number) => ladderSx(SECTION_STEPS, FORM_SECTION_MIN_WIDTH, configured);

function createJsonFormsTheme(
  parentTheme: Theme,
  density: Density = 'compact',
  variant: Variant = 'standard'
): Theme {
  const d = DENSITY_PRESETS[density];
  const isDark = parentTheme.palette.mode === 'dark';
  const labelColor = isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)';

  // Extend the parent theme so palette mode, colors, and background are inherited
  return createTheme(parentTheme, {
    components: {
      MuiTextField: {
        defaultProps: {
          size: 'small',
          margin: 'none',
          variant,
          fullWidth: true,
        },
        styleOverrides: {
          root: {
            marginBottom: d.fieldMarginBottom,
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: 'small',
          margin: 'none',
          variant,
          fullWidth: true,
        },
        styleOverrides: {
          root: {
            marginBottom: d.fieldMarginBottom,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: d.inputFontSize,
            // Make disabled/readOnly fields more readable (MUI default is 0.38 opacity — too faint)
            '&.Mui-disabled': {
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
            },
            '&.Mui-disabled .MuiInputBase-input': {
              WebkitTextFillColor: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
            },
          },
          ...(variant === 'standard'
            ? {
                input: {
                  paddingTop: 2,
                  paddingBottom: 4,
                },
              }
            : {}),
        },
      },
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
        },
        styleOverrides: {
          root: {
            fontSize: d.labelFontSize,
            // Keep label readable when field is disabled
            '&.Mui-disabled': {
              color: labelColor,
            },
            ...(variant === 'standard'
              ? {
                  transform: 'none',
                  position: 'relative',
                  marginBottom: 0,
                  fontWeight: 500,
                  color: labelColor,
                }
              : {}),
          },
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          size: 'small',
          fullWidth: true,
        },
      },
      MuiSelect: {
        defaultProps: {
          size: 'small',
          variant,
        },
      },
    },
  });
}

interface JsonFormsThemeProviderProps {
  children: ReactNode;
  density?: Density;
  variant?: Variant;
}

export const JsonFormsThemeProvider = ({
  children,
  density = 'compact',
  variant = 'standard',
}: JsonFormsThemeProviderProps) => {
  const parentTheme = useTheme();
  const theme = useMemo(
    () => createJsonFormsTheme(parentTheme, density, variant),
    [parentTheme, density, variant]
  );
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
